import { Fragment, PointerEvent, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Comic, ComicImage, RelatedComic } from '../lib/types'
import { captionParts, comicPath, formatDate, imageUrl, tagPath } from '../lib/format'
import { Icon } from './Icon'

/** One panel; if it fails to load, says so with a Reload image button */
export function PanelImage({ image, n, eager, src }: { image: ComicImage; n: number; eager?: boolean; src?: string }) {
  const [failed, setFailed] = useState(false)
  const [attempt, setAttempt] = useState(0)
  const url = src ?? imageUrl(image.key)
  if (failed) {
    return (
      <div className="img-failed">
        <Icon name="image" size={28} />
        <div>This image didn't load</div>
        <button
          type="button"
          className="btn sm"
          onClick={() => {
            setFailed(false)
            setAttempt((a) => a + 1)
          }}
        >
          <Icon name="retry" size={18} />
          Reload image
        </button>
      </div>
    )
  }
  return (
    <img
      className="panel-img"
      src={attempt ? `${url}${url.includes('?') ? '&' : '?'}r=${attempt}` : url}
      alt={image.altText || `Panel ${n}`}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      onError={() => setFailed(true)}
      draggable={false}
    />
  )
}

/** One panel at a time, with arrows, a counter, numbered thumbnails and swiping */
export function Carousel({ images, title, srcs }: { images: ComicImage[]; title: string; srcs?: string[] }) {
  const [i, setI] = useState(0)
  const [dx, setDx] = useState(0)
  const [dragging, setDragging] = useState(false)
  const stage = useRef<HTMLDivElement>(null)
  const drag = useRef<{ x: number; t: number; id: number } | null>(null)
  const n = images.length
  const go = (to: number) => setI(Math.min(n - 1, Math.max(0, to)))
  // The track follows the finger, then snaps to the nearest image like Instagram
  const down = (e: PointerEvent) => {
    if (n < 2 || (e.pointerType === 'mouse' && e.button !== 0) || (e.target as Element).closest('button')) return
    drag.current = { x: e.clientX, t: e.timeStamp, id: e.pointerId }
  }
  const move = (e: PointerEvent) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    let off = e.clientX - d.x
    if (!dragging) {
      if (Math.abs(off) < 6) return
      setDragging(true)
      stage.current?.setPointerCapture(e.pointerId)
    }
    // Resist past the first and last image
    if ((i === 0 && off > 0) || (i === n - 1 && off < 0)) off /= 3
    setDx(off)
  }
  const up = (e: PointerEvent) => {
    const d = drag.current
    if (!d || d.id !== e.pointerId) return
    drag.current = null
    const off = e.clientX - d.x
    const width = stage.current?.clientWidth || 1
    const flick = Math.abs(off) / Math.max(1, e.timeStamp - d.t) > 0.4 && Math.abs(off) > 20
    if (dragging && (Math.abs(off) > width * 0.2 || flick)) go(i + (off < 0 ? 1 : -1))
    setDragging(false)
    setDx(0)
  }
  const cancel = () => {
    drag.current = null
    setDragging(false)
    setDx(0)
  }
  if (n === 0) return null
  return (
    <section aria-label={`${title}: images`} aria-roledescription="carousel">
      <div
        ref={stage}
        className={`carousel-stage${dragging ? ' dragging' : ''}`}
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={cancel}
      >
        <div className="carousel-track" style={{ transform: `translateX(calc(${-i * 100}% + ${dx}px))` }}>
          {images.map((img, k) => (
            <div key={img.key} className="carousel-slide" aria-hidden={k !== i}>
              <PanelImage image={img} n={k + 1} eager={Math.abs(k - i) <= 1} src={srcs?.[k]} />
            </div>
          ))}
        </div>
        {n > 1 && (
          <>
            <button className="arrow prev" type="button" aria-label="Previous image" disabled={i === 0} onClick={() => go(i - 1)}>
              <Icon name="left" size={16} />
            </button>
            <button className="arrow next" type="button" aria-label="Next image" disabled={i === n - 1} onClick={() => go(i + 1)}>
              <Icon name="right" size={16} />
            </button>
          </>
        )}
      </div>
      {n > 1 && (
        <>
          <div className="counter" aria-live="polite">
            Image {i + 1} of {n}
          </div>
          <div className="thumbs">
            {images.map((img, k) => (
              <button key={img.key} type="button" className="thumb" aria-label={`Show image ${k + 1}`} aria-current={k === i} onClick={() => setI(k)}>
                <img src={srcs?.[k] ?? imageUrl(img.key)} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

export function LongForm({ images, srcs }: { images: ComicImage[]; srcs?: string[] }) {
  return (
    <div className="longform">
      {images.map((img, k) => (
        <PanelImage key={img.key} image={img} n={k + 1} eager={k === 0} src={srcs?.[k]} />
      ))}
    </div>
  )
}

/** The panels in the comic's style. Automatic is carousel in feeds, long form on its own page. */
export function ComicImages({ comic, where, srcs }: { comic: Pick<Comic, 'images' | 'displayStyle' | 'title'>; where: 'feed' | 'page'; srcs?: string[] }) {
  const style = comic.displayStyle === 'auto' ? (where === 'feed' ? 'carousel' : 'longForm') : comic.displayStyle
  return style === 'carousel' ? (
    <Carousel images={comic.images} title={comic.title} srcs={srcs} />
  ) : (
    <LongForm images={comic.images} srcs={srcs} />
  )
}

/** Caption text with [[Title]] links turned into links to those comics */
export function Caption({ caption, links = {}, className = 'body-text caption' }: { caption: string; links?: Record<string, string>; className?: string }) {
  if (!caption.trim()) return null
  return (
    <p className={className}>
      {captionParts(caption).map((part, k) =>
        'text' in part ? (
          <Fragment key={k}>{part.text}</Fragment>
        ) : links[part.title.toLowerCase()] ? (
          <Link key={k} to={comicPath(links[part.title.toLowerCase()])}>
            {part.label}
          </Link>
        ) : (
          <Fragment key={k}>{part.label}</Fragment>
        )
      )}
    </p>
  )
}

export function MetaLine({ date, tags }: { date: string; tags: string[] }) {
  return (
    <div className="meta">
      <time dateTime={date}>{formatDate(date)}</time>
      {tags.map((t) => (
        <Link key={t} className="chip" to={tagPath(t)}>
          {t}
        </Link>
      ))}
    </div>
  )
}

/** A full comic in the home, tag or search feed */
export function FeedPost({ comic }: { comic: Comic }) {
  return (
    <article className="post">
      <h2 className="post-title">
        <Link to={comicPath(comic.slug)}>{comic.title}</Link>
      </h2>
      <MetaLine date={comic.date} tags={comic.tags} />
      <ComicImages comic={comic} where="feed" />
      <Caption caption={comic.caption} links={comic.links} />
      <Link to={comicPath(comic.slug)} className="textlink">
        View comic page and comments
        <Icon name="right" size={18} />
      </Link>
    </article>
  )
}

export function Feed({ comics }: { comics: Comic[] }) {
  return (
    <>
      {comics.map((c, k) => (
        <Fragment key={c.id}>
          {k > 0 && <hr className="post-sep" />}
          <FeedPost comic={c} />
        </Fragment>
      ))}
    </>
  )
}

/** Newer, numbered pages, Older */
/** Comics as thumbnails with titles: related comics, and tag pages set to a grid */
export function ComicGrid({ comics }: { comics: RelatedComic[] }) {
  return (
    <div className="related-grid">
      {comics.map((r) => (
        <Link key={r.slug} className="rel" to={comicPath(r.slug)}>
          <div className={r.images.length < 4 ? 'collage one' : 'collage'} aria-hidden="true">
            {(r.images.length < 4 ? r.images.slice(0, 1) : r.images.slice(0, 4)).map((img) => (
              <span key={img.key}>
                <img src={imageUrl(img.key)} alt="" loading="lazy" />
              </span>
            ))}
          </div>
          <span className="rt">{r.title}</span>
        </Link>
      ))}
    </div>
  )
}

export function Pagination({ page, totalPages, href }: { page: number; totalPages: number; href: (page: number) => string }) {
  if (totalPages <= 1) return null
  const first = Math.max(1, Math.min(page - 2, totalPages - 4))
  const pages = Array.from({ length: Math.min(5, totalPages) }, (_, k) => first + k)
  return (
    <nav aria-label="Pages" className="pagination">
      {page > 1 ? (
        <Link className="pg step" to={href(page - 1)} aria-label="Newer comics">
          <Icon name="left" size={18} />
          <span className="pg-label">Newer</span>
        </Link>
      ) : (
        <span className="pg disabled" aria-hidden="true">
          <Icon name="left" size={18} />
          <span className="pg-label">Newer</span>
        </span>
      )}
      {pages.map((p) => (
        <Link key={p} className="pg" to={href(p)} aria-current={p === page ? 'page' : undefined} aria-label={`Page ${p}`}>
          {p}
        </Link>
      ))}
      {page < totalPages ? (
        <Link className="pg step" to={href(page + 1)} aria-label="Older comics">
          <span className="pg-label">Older</span>
          <Icon name="right" size={18} />
        </Link>
      ) : (
        <span className="pg disabled" aria-hidden="true">
          <span className="pg-label">Older</span>
          <Icon name="right" size={18} />
        </span>
      )}
    </nav>
  )
}
